import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  message
} from "antd";
import {
  createSprNumberCat,
  fetchSprNumberCat,
  fetchSprNumberCatMealTypes,
  saveSprNumberCat
} from "./spr-number-cat-api";

const LABEL_WIDTH = 200;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toFlag = (value) => Number(value) === 1;

const normalizeCard = (row) => ({
  idNumberCat: toNumber(pick(row, "idNumberCat", "IdNumberCat")),
  name: pick(row, "name", "Name") ?? "",
  cntMest: toNumber(pick(row, "cntMest", "CntMest")) ?? 1,
  cntDopMest: toNumber(pick(row, "cntDopMest", "CntDopMest")) ?? 0,
  isAdd: toFlag(pick(row, "isAdd", "IsAdd")),
  dayChange: toNumber(pick(row, "dayChange", "DayChange")),
  vidPitania: toNumber(pick(row, "vidPitania", "VidPitania")) ?? 0,
  isDel: toFlag(pick(row, "isDel", "IsDel"))
});

const cardToFormValues = (card) => ({
  idNumberCat: card.idNumberCat,
  name: card.name,
  cntMest: card.cntMest,
  cntDopMest: card.cntDopMest,
  isAdd: Boolean(card.isAdd),
  dayChange: card.dayChange,
  vidPitania: card.vidPitania && card.vidPitania > 0 ? card.vidPitania : 0,
  isDel: Boolean(card.isDel)
});

const emptyCard = () => ({
  idNumberCat: null,
  name: "",
  cntMest: 1,
  cntDopMest: 0,
  isAdd: false,
  dayChange: null,
  vidPitania: 0,
  isDel: false
});

/**
 * Форма карточки категории номера. Delphi: UESprNumberCat.
 */
const SprNumberCatCard = ({ idNumberCat, isNew = false, open, onSaved, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mealTypes, setMealTypes] = useState([]);

  const mealOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...(mealTypes || [])
        .map((m) => ({
          value: toNumber(pick(m, "id", "Id")),
          label: pick(m, "name", "Name") ?? ""
        }))
        .filter((o) => o.value != null && o.value > 0)
    ],
    [mealTypes]
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchSprNumberCatMealTypes()
      .then((data) => {
        if (!cancelled) setMealTypes(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setMealTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      form.setFieldsValue(cardToFormValues(emptyCard()));
      return () => {
        cancelled = true;
      };
    }

    if (idNumberCat == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprNumberCat(idNumberCat)
      .then((row) => {
        if (cancelled) return;
        form.setFieldsValue(cardToFormValues(normalizeCard(row)));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить категорию");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idNumberCat, form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      name: (values.name || "").trim(),
      cntMest: values.cntMest ?? 1,
      cntDopMest: values.cntDopMest ?? 0,
      isAdd: values.isAdd ? 1 : 0,
      dayChange: values.dayChange ?? null,
      vidPitania: values.vidPitania && values.vidPitania > 0 ? values.vidPitania : 0,
      isDel: values.isDel ? 1 : 0
    };

    try {
      if (isNew) {
        const created = await createSprNumberCat(body);
        message.success("Сохранено");
        onSaved?.(created);
      } else {
        const saved = await saveSprNumberCat({ ...body, idNumberCat });
        message.success("Сохранено");
        onSaved?.(saved);
      }
      onCancel?.();
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-number-cat-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-number-cat-card">
      <div className="spr-number-cat-card__body">
        {error ? (
          <Alert className="spr-number-cat-card__error" type="error" showIcon message={error} />
        ) : null}
        <Form
          form={form}
          className="spr-number-cat-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          {!isNew ? (
            <Form.Item label="#" name="idNumberCat">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Количество мест" name="cntMest">
            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
          </Form.Item>
          <Form.Item label="Количество доп. мест" name="cntDopMest">
            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
          </Form.Item>
          <Form.Item label="Подселение" name="isAdd" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item label="Смена белья, дней" name="dayChange">
            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
          </Form.Item>
          <Form.Item label="Вид питания" name="vidPitania">
            <Select options={mealOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Скрыть" name="isDel" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Form>
      </div>
      <div className="spr-number-cat-card__footer">
        <Button type="primary" loading={saving} onClick={() => form.submit()}>
          ОК
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprNumberCatCard;
